import { useEffect, useState } from "react";
import { Card, CardContent } from "../../components/ui/card";
import { Play } from "lucide-react";
import SEO from "../SEO";

const ITEMS_PER_PAGE = 12;
const BASE_URL =
  "/wp-json/youtube/v1/shorts";


export default function VideosPage() {

  const videosPageSchema = [
    {
      "@context": "https://schema.org",
      "@type": "MedicalOrganization",
      "name": "Pranaam Hospitals",
      "url": "https://pranaamhospitals.com/",
      "logo": "https://pranaamhospitals.com/wp-content/uploads/2025/logo.png"
    }
  ];

  const [videos, setVideos] = useState([]);
  const [page, setPage] = useState(2);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [seo, setSeo] = useState({});
  const [pageKeywords, setPageKeywords] = useState("");

  // ✅ STATIC LOAD (ONLY FIRST 12)
  useEffect(() => {
    const loadInitial = async () => {
      try {
        const res = await fetch(
          `${BASE_URL}?per_page=${ITEMS_PER_PAGE}&page=1`
        );
        const data = await res.json();

        const formatted = data.map((item) => ({
          id: item.id,
          title: item.title,
          thumbnail: item.thumbnail,
          videoUrl: `https://www.youtube.com/watch?v=${item.id}`,
          doctor: "Pranaam Hospitals",
        }));

        setVideos(formatted);

        if (!data || data.length < ITEMS_PER_PAGE) {
          setHasMore(false);
        }
      } catch (err) {
        console.error("INITIAL FETCH ERROR:", err);
        setHasMore(false);
      }

      setInitialLoading(false);
    };

    loadInitial();

    fetch("/wp-json/custom/v1/page-seo?slug=videos")
      .then(res => res.json())
      .then(data => { if (data?.seo) setSeo(data.seo); setPageKeywords(data?.keywords || ""); })
      .catch(() => {});
  }, []);

  // ✅ DYNAMIC LOAD MORE (page 2, 3, 4...)
  const handleLoadMore = async () => {
    if (loading || !hasMore) return;

    setLoading(true);

    try {
      const res = await fetch(
        `${BASE_URL}?per_page=${ITEMS_PER_PAGE}&page=${page}`
      );

      const data = await res.json();

      if (!data || data.length < ITEMS_PER_PAGE) {
        setHasMore(false);
      }

      const formatted = data.map((item) => ({
        id: item.id,
        title: item.title,
        thumbnail: item.thumbnail,
        videoUrl: `https://www.youtube.com/watch?v=${item.id}`,
        doctor: "Pranaam Hospitals",
      }));

      setVideos((prev) => [...prev, ...formatted]);
      setPage((prev) => prev + 1);

    } catch (err) {
      console.error("LOAD MORE ERROR:", err);
      setHasMore(false);
    }

    setLoading(false);
  };

  if (initialLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="animate-spin rounded-full h-16 w-16 border-4 border-[#951C25] border-t-transparent"></div>
      </div>
    );
  }

  return (
    <>
      <SEO
        title={seo.title || "Medical Videos & Health Resources | Pranaam Hospitals Hyderabad"}
        description={seo.description || "Watch health and medical videos from Pranaam Hospitals including expert talks, wellness tips, procedure explainers, and patient stories."}
        canonical={seo.canonical || "https://pranaamhospitals.com/videos"}
        robots={Array.isArray(seo.robots) ? seo.robots.join(", ") : seo.robots || "index, follow"}
        keywords={pageKeywords}
        ogTitle={seo.og_title}
        ogDescription={seo.og_description}
        ogImage={seo.og_image || seo.featured_image}
        twitterTitle={seo.twitter_title}
        twitterDescription={seo.twitter_description}
        author="Pranaam Hospitals"
        publisher="Pranaam Hospitals"
        schema={videosPageSchema}
      />

      <main className="min-h-screen bg-white">

        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-[#951C25] to-black py-16 md:py-24">
          <div className="container mx-auto px-4 text-center">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              Health Education Videos
            </h1>
            <p className="text-lg md:text-xl text-white/90">
              Expert medical advice and health tips from our doctors
            </p>
          </div>
        </section>

        {/* Videos Grid */}
        <section className="py-16">
          <div className="container mx-auto px-4 max-w-7xl">

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {videos.map((video) => (
                <Card
                  key={video.id}
                  className="group cursor-pointer border-none shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden"
                  onClick={() => window.open(video.videoUrl, "_blank")}
                >
                  <CardContent className="p-0">
                    <div className="relative h-48 overflow-hidden bg-gray-100">
                      <img
                        src={video.thumbnail}
                        alt={video.title}
                        loading="lazy"
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                      />
                      <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                        <div className="w-16 h-16 rounded-full bg-white flex items-center justify-center">
                          <Play className="w-8 h-8 text-[#951C25] ml-1" fill="currentColor" />
                        </div>
                      </div>
                    </div>

                    <div className="p-4">
                      <h2 className="font-bold text-gray-900 mb-2 line-clamp-2 group-hover:text-[#951C25] transition-colors">
                        {video.title}
                      </h2>
                      <p className="text-sm text-gray-600">
                        {video.doctor}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              ))}
              {loading && (
                [...Array(4)].map((_, i) => (
                  <div
                    key={`skeleton-${i}`}
                    className="animate-pulse bg-gray-200 h-72 rounded-lg"
                  />
                ))
              )}
            </div>

            {/* Load More */}
            {hasMore && (
              <div className="flex justify-center mt-12">
                <button
                  onClick={handleLoadMore}
                  disabled={loading}
                  className="px-8 py-3 bg-[#951C25] text-white font-semibold rounded-lg hover:bg-[#7d1720] transition-colors disabled:opacity-50"
                >
                  {loading ? "Loading..." : "Load More"}
                </button>
              </div>
            )}

          </div>
        </section>
      </main>
    </>
  );
}